import { FloatingPositionAdapter } from "../adapters/FloatingPositionAdapter";

interface TooltipDocument extends Document {
    _adminTooltipInited?: boolean;
}

let tooltip: HTMLElement | null = null;
let tooltipRoot: HTMLElement | null = null;
let cleanup: (() => void) | null = null;
let activeTarget: HTMLElement | null = null;
let openTimer: number | null = null;

function visibleText(target: HTMLElement): string {
    const clone = target.cloneNode(true);
    if (!(clone instanceof HTMLElement)) return "";
    clone.querySelectorAll("[hidden], [aria-hidden='true'], svg, i").forEach((node) => node.remove());
    return (clone.textContent ?? "").trim().replace(/\s+/g, " ");
}

function isIconOnlyButton(target: HTMLElement): target is HTMLButtonElement {
    return target instanceof HTMLButtonElement && visibleText(target) === "";
}

function tooltipText(target: HTMLElement): string {
    const explicit = target.dataset.tooltip ?? "";
    const nativeTitle = target.dataset.nativeTitle ?? target.getAttribute("title") ?? "";
    const aria = target.getAttribute("aria-label") ?? "";
    return (explicit || nativeTitle || aria).trim().slice(0, 120);
}

function preserveAndRemoveTitle(target: HTMLElement): void {
    if (!target.hasAttribute("title")) return;
    target.dataset.nativeTitle = target.getAttribute("title") ?? "";
    target.removeAttribute("title");
}

function suppressNativeTooltip(node: EventTarget | null): void {
    if (!(node instanceof HTMLElement)) return;
    const target = node.closest<HTMLButtonElement>("button[title]");
    if (!target || !isIconOnlyButton(target)) return;
    preserveAndRemoveTitle(target);
}

function candidate(node: EventTarget | null): HTMLButtonElement | null {
    if (!(node instanceof HTMLElement)) return null;
    const target = node.closest<HTMLButtonElement>("button[data-tooltip], button[title], button[data-native-title], button[aria-label]");
    if (!target || target.matches("[disabled], [aria-disabled='true']")) return null;
    if (!isIconOnlyButton(target)) return null;
    return tooltipText(target) !== "" ? target : null;
}

function ensureTooltipRoot(): HTMLElement {
    if (tooltipRoot) return tooltipRoot;
    tooltipRoot = document.querySelector<HTMLElement>("[data-admin-tooltip-root]");
    if (tooltipRoot) return tooltipRoot;
    tooltipRoot = document.createElement("div");
    tooltipRoot.className = "admin-tooltip-root";
    tooltipRoot.dataset.adminTooltipRoot = "1";
    document.body.appendChild(tooltipRoot);
    return tooltipRoot;
}

function ensureTooltip(): HTMLElement {
    if (tooltip) return tooltip;
    tooltip = document.createElement("div");
    tooltip.className = "admin-tooltip";
    tooltip.id = "admin-tooltip";
    tooltip.setAttribute("role", "tooltip");
    tooltip.hidden = true;
    const arrow = document.createElement("span");
    arrow.className = "admin-tooltip__arrow";
    arrow.dataset.adminTooltipArrow = "1";
    tooltip.appendChild(arrow);
    ensureTooltipRoot().appendChild(tooltip);
    return tooltip;
}

async function place(target: HTMLElement, tip: HTMLElement): Promise<void> {
    cleanup?.();
    const arrow = tip.querySelector<HTMLElement>("[data-admin-tooltip-arrow]");
    cleanup = FloatingPositionAdapter.auto(target, tip, {
        placement: placementFor(target),
        offset: 8,
        shiftPadding: 8,
        arrowElement: arrow,
    });
}

function placementFor(target: HTMLElement): "top" | "right" | "bottom" {
    if (target.closest(".admin-cms-builder__left, [data-cms-left-panel], [data-cms-tree-host]")) {
        return "right";
    }
    if (target.closest(".admin-drawer__figma-topbar, .admin-header, .admin-cms-builder__toolbar")) {
        return "bottom";
    }
    return "top";
}

function show(target: HTMLElement): void {
    const text = tooltipText(target);
    if (text === "") return;
    preserveAndRemoveTitle(target);

    const tip = ensureTooltip();
    let label = tip.querySelector<HTMLElement>("[data-admin-tooltip-label]");
    if (!label) {
        label = document.createElement("span");
        label.className = "admin-tooltip__label";
        label.dataset.adminTooltipLabel = "1";
        tip.prepend(label);
    }
    label.textContent = text;
    tip.hidden = false;
    activeTarget = target;
    target.setAttribute("aria-describedby", tip.id);
    void place(target, tip);
}

function hide(target?: HTMLElement | null): void {
    if (openTimer !== null) {
        window.clearTimeout(openTimer);
        openTimer = null;
    }
    if (target && activeTarget && target !== activeTarget) return;
    cleanup?.();
    cleanup = null;
    if (activeTarget) {
        activeTarget.removeAttribute("aria-describedby");
    }
    activeTarget = null;
    if (tooltip) {
        tooltip.hidden = true;
        const label = tooltip.querySelector<HTMLElement>("[data-admin-tooltip-label]");
        if (label) label.textContent = "";
    }
}

function queueShow(target: HTMLElement): void {
    if (openTimer !== null) window.clearTimeout(openTimer);
    openTimer = window.setTimeout(() => {
        openTimer = null;
        show(target);
    }, 160);
}

export function initAdminTooltips(root: TooltipDocument = document): void {
    if (root._adminTooltipInited) return;
    root._adminTooltipInited = true;

    root.addEventListener("mouseover", (event) => {
        suppressNativeTooltip(event.target);
        const target = candidate(event.target);
        if (target) queueShow(target);
    });
    root.addEventListener("mouseout", (event) => {
        const target = candidate(event.target);
        if (!target) return;
        const next = event.relatedTarget;
        if (next instanceof Node && target.contains(next)) return;
        hide(target);
    });
    root.addEventListener("focusin", (event) => {
        suppressNativeTooltip(event.target);
        const target = candidate(event.target);
        if (target) queueShow(target);
    });
    root.addEventListener("focusout", (event) => {
        hide(candidate(event.target));
    });
    root.addEventListener("keydown", (event) => {
        if (event.key === "Escape") hide();
    });
}
